import {
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EndpointUrl } from 'api/endpoint';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useCurrentModal, useModal } from 'components/modal';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FinsightEntity, FinsightEntityField } from 'shared';
import { formatName } from 'utils/fields';

function EntitySelectionModal({
  field,
  currentValue,
  onSelect,
}: {
  field: FinsightEntityField;
  currentValue: string | undefined;
  onSelect: (value: string | undefined) => void;
}) {
  const modalCtx = useCurrentModal();
  const workspaceId = useWorkspaceId();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    currentValue,
  );
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<Record<string, FinsightEntity>>({});

  useEffect(() => {
    setLoading(true);
    // fetch data
    fetch(
      `${EndpointUrl}/workspace/${workspaceId}/entities/all?withFields=false`,
      {
        method: 'POST',
        headers: {
          'x-finsight-token': localStorage.getItem('token') || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(field.schema.constraints ?? []),
      },
    )
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const map: Record<string, FinsightEntity> = {};

          for (const _entity of data) {
            const entity = _entity as FinsightEntity;
            map[entity.id] = entity;
          }

          setOptions(map);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [field.schema.constraints, workspaceId]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return Object.keys(options);
    }

    const query = searchQuery.toLowerCase();
    return Object.keys(options).filter((key) => {
      const entity = options[key];
      return (
        entity.name?.toLowerCase().includes(query) ||
        entity.description?.toLowerCase().includes(query) ||
        key.toLowerCase().includes(query)
      );
    });
  }, [options, searchQuery]);

  const handleUpdate = async () => {
    if (onSelect) {
      await onSelect(selectedValue);
    }
    modalCtx?.closeModal();
  };

  const handleCancel = () => {
    modalCtx?.closeModal();
  };

  return (
    <Stack gap={2} sx={{ minWidth: 400, maxWidth: 600, maxHeight: '70vh' }}>
      <TextField
        fullWidth
        placeholder="Search entities..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoFocus
      />
      {loading ? (
        <Stack alignItems="center" p={4}>
          <CircularProgress />
        </Stack>
      ) : (
        <List
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedValue === undefined}
              onClick={() => setSelectedValue(undefined)}
            >
              <ListItemText primary="(None)" secondary="Clear selection" />
            </ListItemButton>
          </ListItem>
          {filteredOptions.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No entities found"
                secondary={
                  searchQuery
                    ? 'Try a different search term'
                    : 'No entities available'
                }
              />
            </ListItem>
          ) : (
            filteredOptions.map((key) => {
              const entity = options[key];
              const isSelected = selectedValue === key;
              return (
                <ListItem key={key} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setSelectedValue(key)}
                  >
                    <ListItemText
                      primary={entity.name ?? key}
                      secondary={entity.description}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
      )}
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleUpdate} variant="contained">
          Update
        </Button>
      </Stack>
    </Stack>
  );
}

export function EntityRenderer({
  field,
  value: _value,
  editable,
  onChange,
}: {
  field: FinsightEntityField;
  value: unknown;
  editable?: boolean;
  onChange?: (value: unknown) => Promise<void>;
}) {
  const workspaceId = useWorkspaceId();
  const value = _value ?? undefined;
  const { showModal } = useModal();

  const initFetched = useRef(false);
  const [displayOptions, setDisplayOptions] = useState<
    Record<string, FinsightEntity>
  >({});

  useEffect(() => {
    if (
      !initFetched.current &&
      value !== undefined &&
      typeof value === 'string'
    ) {
      initFetched.current = true;
      fetch(
        `${EndpointUrl}/workspace/${workspaceId}/entity/${value}?withFields=false`,
        {
          headers: {
            'x-finsight-token': localStorage.getItem('token') || '',
          },
        },
      )
        .then((response) => response.json())
        .then((data) => {
          if (data) {
            setDisplayOptions({ [value]: data });
          }
        });
    }
  }, [value, workspaceId]);

  const handleOpenModal = () => {
    if (typeof value !== 'string' && value !== null && value !== undefined)
      return;
    showModal(
      `Select ${formatName(field.name)}`,
      <EntitySelectionModal
        field={field}
        currentValue={value}
        onSelect={async (newValue) => {
          if (onChange) {
            await onChange(newValue);
          }
        }}
      />,
    );
  };

  if (value !== undefined && typeof value !== 'string') {
    return null;
  }

  if (editable) {
    const displayValue = value ? (displayOptions[value]?.name ?? value) : '';
    return (
      <TextField
        fullWidth
        label={formatName(field.name)}
        value={displayValue}
        onClick={handleOpenModal}
        InputProps={{
          readOnly: true,
        }}
        placeholder="Click to select entity"
      />
    );
  }

  return <Typography>{value}</Typography>;
}
